import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useAppData } from '../context/AppDataContext';
import { useUser } from '../context/UserContext';
import { useTheme } from '../context/ThemeProvider';
import { useProfilePhotoUpload } from '../hooks/useProfilePhotoUpload';
import { APP_VERSION } from '../version';
import { LanguageSwitch } from './LanguageSwitch';
import { SiteFooter } from './SiteFooter';
import { ViewFadeIn } from './ViewFadeIn';
import { KpiExplainProvider } from './KpiExplainProvider';
import { BackgroundTaskToastStack } from './BackgroundTaskToastStack';
import { AnalysisJobsProvider } from '../context/AnalysisJobsContext';
import { OpportunityDiscoveryProvider } from '../context/OpportunityDiscoveryContext';
import { OpportunityHardeningProvider } from '../context/OpportunityHardeningContext';
import { OpportunityPnlGenerationProvider } from '../context/OpportunityPnlGenerationContext';
import { OpportunityRoadmapGenerationProvider } from '../context/OpportunityRoadmapGenerationContext';
import styles from './AppLayout.module.css';

function headerAvatarInitials(
  firstName: string,
  lastName: string,
  email: string | null
): string {
  const f = firstName.trim();
  const l = lastName.trim();
  if (f && l) return `${f[0]!}${l[0]!}`.toUpperCase();
  if (f.length >= 2) return f.slice(0, 2).toUpperCase();
  if (f) return f.slice(0, 1).toUpperCase();
  if (email) {
    const local = email.split('@')[0] ?? '';
    if (local.length >= 2) return local.slice(0, 2).toUpperCase();
    if (local) return local.slice(0, 1).toUpperCase();
  }
  return '?';
}

function ThemeToggleIcon({ theme }: { theme: 'light' | 'dark' }) {
  if (theme === 'light') {
    return (
      <svg className={styles.themeToggleIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M20 14.5A8.5 8.5 0 0 1 9.5 4 7 7 0 1 0 20 14.5Z"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg className={styles.themeToggleIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function AppLayout() {
  const { theme, toggleTheme } = useTheme();
  const { pathname } = useLocation();
  const { companies, investmentCases, financialItems, bankAccounts, realEstateItems, businessOpportunities, legalEntities } = useAppData();
  const { user, email, firstName, lastName, avatar, tasks } = useUser();
  const { uploading: avatarUploading, upload: uploadAvatar } = useProfilePhotoUpload(user?.uid);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isDarkMode = theme === 'dark';

  const nav: Array<{
    to: string;
    label: string;
    icon: string;
    count?: number;
    isActive: boolean;
  }> = [
    {
      to: '/home',
      label: 'Net worth',
      icon: isDarkMode ? '/assets/icons/chart.pie.svg' : '/assets/icons/chart.pie.black.svg',
      isActive: pathname === '/' || pathname === '/home',
    },
    {
      to: '/investments',
      label: 'Investments',
      icon: isDarkMode ? '/assets/icons/building.2.svg' : '/assets/icons/building.2.black.svg',
      count: companies.length,
      isActive: pathname.startsWith('/investments') && !pathname.startsWith('/investment-cases'),
    },
    {
      to: '/investment-cases',
      label: 'Dealflow',
      icon: isDarkMode
        ? '/assets/icons/document.on.document.svg'
        : '/assets/icons/document.on.document.black.svg',
      count: investmentCases.length,
      isActive: pathname.startsWith('/investment-cases'),
    },
    {
      to: '/opportunities',
      label: 'Opportunities',
      icon: isDarkMode
        ? '/assets/icons/pencil.tip.crop.circle.svg'
        : '/assets/icons/pencil.tip.crop.circle.black.svg',
      count: businessOpportunities?.length ?? 0,
      isActive: pathname.startsWith('/opportunities'),
    },
    {
      to: '/assets',
      label: 'Assets & Liabilities',
      icon: isDarkMode ? '/assets/icons/list.bullet.svg' : '/assets/icons/list.bullet.black.svg',
      count: financialItems.length,
      isActive: pathname.startsWith('/assets'),
    },
    {
      to: '/bank-accounts',
      label: 'Bank accounts',
      icon: isDarkMode ? '/assets/icons/creditcard.svg' : '/assets/icons/creditcard.black.svg',
      count: bankAccounts?.length ?? 0,
      isActive: pathname.startsWith('/bank-accounts'),
    },
    {
      to: '/real-estate',
      label: 'Real estate',
      icon: isDarkMode ? '/assets/icons/house.svg' : '/assets/icons/house.black.svg',
      count: realEstateItems?.length ?? 0,
      isActive: pathname.startsWith('/real-estate'),
    },
    {
      to: '/entities',
      label: 'Legal entities',
      icon: isDarkMode
        ? '/assets/icons/point.3.connected.trianglepath.dotted.svg'
        : '/assets/icons/point.3.connected.trianglepath.dotted.black.svg',
      count: legalEntities?.length ?? 0,
      isActive: pathname.startsWith('/entities'),
    },
    {
      to: '/scenarios',
      label: 'Scenarios',
      icon: isDarkMode
        ? '/assets/icons/slider.horizontal.3.svg'
        : '/assets/icons/slider.horizontal.3.black.svg',
      isActive: pathname.startsWith('/scenarios'),
    },
    {
      to: '/tasks',
      label: 'Tasks',
      icon: isDarkMode
        ? '/assets/icons/checkmark.circle.svg'
        : '/assets/icons/checkmark.circle.black.svg',
      count: tasks?.filter((task) => !task.done).length ?? 0,
      isActive: pathname.startsWith('/tasks'),
    },
    {
      to: '/intelligence',
      label: 'Intelligence',
      icon: isDarkMode
        ? '/assets/icons/apple.intelligence.svg'
        : '/assets/icons/apple.intelligence.green.svg',
      isActive: pathname.startsWith('/intelligence'),
    },
    {
      to: '/skills',
      label: 'Skills',
      icon: isDarkMode ? '/assets/icons/medal.star.svg' : '/assets/icons/medal.star.orange.svg',
      isActive: pathname.startsWith('/skills'),
    },
    {
      to: '/settings',
      label: 'Settings',
      icon: isDarkMode ? '/assets/icons/gearshape.svg' : '/assets/icons/gearshape.black.svg',
      isActive: pathname.startsWith('/settings'),
    },
  ];

  const year = new Date().getFullYear();

  const namePart = [firstName, lastName].filter(Boolean).join(' ').trim();

  const titleText = useMemo(() => {
    if (namePart) {
      return email ? `${namePart} (${email})` : namePart;
    }
    if (email) return email;
    return 'Signed in';
  }, [namePart, email]);

  const photoSrc = (avatar?.trim() || user?.photoURL || '').trim();
  const showPhoto = Boolean(photoSrc) && !avatarBroken;
  const initials = headerAvatarInitials(firstName ?? '', lastName ?? '', email);

  useEffect(() => {
    setAvatarBroken(false);
  }, [photoSrc]);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error('Sign out failed', e);
    }
  };

  const brandBlock = (
    <>
      <img
        className={styles.brandLogo}
        src="/assets/perfina-icon.png"
        alt=""
        width={32}
        height={32}
      />
      <div className={styles.brandText}>
        <span className={styles.brandTitle}>Halfmeyer</span>
        <span className={styles.brandSub}>Wealth Manager</span>
      </div>
    </>
  );

  return (
    <OpportunityDiscoveryProvider>
    <OpportunityPnlGenerationProvider>
    <OpportunityRoadmapGenerationProvider>
    <OpportunityHardeningProvider>
    <AnalysisJobsProvider>
    <div className={styles.shell}>
      <header className={styles.mobileHeader}>
        <div className={styles.brand}>{brandBlock}</div>
        <button
          type="button"
          className={styles.menuToggle}
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          aria-controls="app-nav-drawer"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        >
          <svg className={styles.menuToggleIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            {menuOpen ? (
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            ) : (
              <>
                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
              </>
            )}
          </svg>
        </button>
      </header>

      {menuOpen ? (
        <button
          type="button"
          className={styles.sidebarOverlay}
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        />
      ) : null}

      <aside
        id="app-nav-drawer"
        className={`${styles.sidebar} ${styles.sidebarEnter} ${menuOpen ? styles.sidebarOpen : ''}`}
        aria-label="Main navigation"
      >
        <div className={styles.brand}>
          {brandBlock}
        </div>
        <nav className={styles.nav}>
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              aria-current={item.isActive ? 'page' : undefined}
              onClick={() => setMenuOpen(false)}
              className={() =>
                `${styles.navItem} ${item.isActive ? styles.navItemActive : ''}`
              }
            >
              <span className={styles.navItemMain}>
                <img
                  className={styles.navIcon}
                  src={item.icon}
                  alt=""
                  width={14}
                  height={14}
                  aria-hidden
                />
                <span className={styles.navItemLabel}>{item.label}</span>
              </span>
              {item.count !== undefined ? (
                <span className="app-counter">{item.count}</span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <footer className={styles.foot}>
          <div className={styles.footLinks}>
            <a
              href="https://halfmeyer.com/imprint.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Imprint
            </a>
            <span className={styles.footSep} aria-hidden>
              ·
            </span>
            <a
              href="https://halfmeyer.com/privacy.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy
            </a>
          </div>
          <p className={styles.footCopy}>
            © Halfmeyer Ventures {year}
            <span className={styles.version}> v{APP_VERSION}</span>
          </p>
        </footer>
      </aside>
      <div className={`${styles.mainCol} ${styles.mainColEnter}`}>
        <header className={styles.topBar}>
          <div className={styles.userRow}>
            <label
              className={styles.userAvatarUpload}
              title="Upload profile picture"
            >
              <input
                type="file"
                accept="image/*"
                className={styles.userAvatarInput}
                disabled={avatarUploading}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void uploadAvatar(file);
                  e.target.value = '';
                }}
              />
              <span className={styles.userAvatar}>
                {showPhoto ? (
                  <img
                    className={styles.userAvatarImg}
                    src={photoSrc}
                    alt=""
                    onError={() => setAvatarBroken(true)}
                  />
                ) : (
                  <span className={styles.userAvatarFallback}>{initials}</span>
                )}
                <span className={styles.userAvatarOverlay} aria-hidden>
                  {avatarUploading ? (
                    <span className={styles.userAvatarSpinner} />
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2l1.2-1.8A1 1 0 0 1 8.5 4.7h7a1 1 0 0 1 .8.5L17.5 7h2A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-9Z"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12.5" r="3" stroke="currentColor" strokeWidth="1.75" />
                    </svg>
                  )}
                </span>
              </span>
            </label>
            <span className={styles.userMeta} title={titleText}>
              {namePart ? (
                <>
                  <span className={styles.userName}>{namePart}</span>
                  {email ? <span className={styles.userEmail}>{email}</span> : null}
                </>
              ) : (
                <span className={styles.userName}>{email || 'Signed in'}</span>
              )}
            </span>
          </div>
          <div className={styles.topBarActions}>
            <LanguageSwitch />
            <button
              type="button"
              className={styles.themeToggle}
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-pressed={theme === 'light'}
            >
              <ThemeToggleIcon theme={theme} />
            </button>
            <button
              type="button"
              className={styles.themeToggle}
              onClick={handleSignOut}
              aria-label="Sign out"
            >
              <img
                className={styles.themeToggleIcon}
                src={
                  isDarkMode
                    ? '/assets/icons/arrow.right.circle.svg'
                    : '/assets/icons/arrow.right.circle.black.svg'
                }
                alt=""
                width={16}
                height={16}
                aria-hidden
              />
            </button>
          </div>
        </header>
        <div className={styles.outlet}>
          <KpiExplainProvider>
            <ViewFadeIn key={pathname}>
              <Outlet />
            </ViewFadeIn>
          </KpiExplainProvider>
        </div>
        <div className={styles.mobileFooter}>
          <SiteFooter className={styles.mobileFooterInner} />
        </div>
      </div>
      <BackgroundTaskToastStack />
    </div>
    </AnalysisJobsProvider>
    </OpportunityHardeningProvider>
    </OpportunityRoadmapGenerationProvider>
    </OpportunityPnlGenerationProvider>
    </OpportunityDiscoveryProvider>
  );
}
